/** Auth state and OIDC flow (Svelte 5 runes). */

import {
  OIDC_CLIENT_ID,
  OIDC_AUTHORIZE_URL,
  OIDC_TOKEN_URL,
  OIDC_END_SESSION_URL,
  OIDC_REDIRECT_URI,
  OIDC_SCOPES,
} from './config';
import { generateCodeVerifier, generateCodeChallenge } from './pkce';
import { log } from '$lib/log';

let accessToken = $state('');
let expiresAt = $state(0);
let userName = $state('');

function parseJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
}

function storeSession(token: string, expiresIn: number) {
  accessToken = token;
  expiresAt = Date.now() + expiresIn * 1000;
  const payload = parseJwtPayload(token);
  userName = (payload.preferred_username as string) ?? '';
  localStorage.setItem('oidc_access_token', token);
  localStorage.setItem('oidc_expires_at', String(expiresAt));
  localStorage.setItem('oidc_user_name', userName);
}

function clearSession() {
  accessToken = '';
  expiresAt = 0;
  userName = '';
  localStorage.removeItem('oidc_access_token');
  localStorage.removeItem('oidc_expires_at');
  localStorage.removeItem('oidc_user_name');
}

export function restoreSession() {
  const token = localStorage.getItem('oidc_access_token');
  const exp = Number(localStorage.getItem('oidc_expires_at') ?? '0');
  if (token && exp > Date.now()) {
    accessToken = token;
    expiresAt = exp;
    userName = localStorage.getItem('oidc_user_name') ?? '';
  } else {
    clearSession();
  }
}

export async function login() {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  localStorage.setItem('oidc_code_verifier', verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: OIDC_CLIENT_ID,
    redirect_uri: OIDC_REDIRECT_URI,
    scope: OIDC_SCOPES,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${OIDC_AUTHORIZE_URL}?${params}`;
}

export async function handleCallback(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const verifier = localStorage.getItem('oidc_code_verifier');

  log.debug('[auth] callback params:', { code: code?.slice(0, 8) + '…', hasVerifier: !!verifier });

  if (!code || !verifier) {
    log.error('[auth] Missing code or verifier', { code: !!code, verifier: !!verifier });
    return false;
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: OIDC_CLIENT_ID,
    code,
    redirect_uri: OIDC_REDIRECT_URI,
    code_verifier: verifier,
  });

  log.debug('[auth] Exchanging code at', OIDC_TOKEN_URL, 'redirect_uri=', OIDC_REDIRECT_URI);

  const res = await fetch(OIDC_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    throw new Error(`OIDC token exchange failed: ${res.status} ${await res.text()}`);
  }
  const data = await res.json();
  storeSession(data.access_token, data.expires_in ?? 3600);
  localStorage.removeItem('oidc_code_verifier');
  return true;
}

export function logout() {
  const token = accessToken;
  clearSession();
  const params = new URLSearchParams({
    post_logout_redirect_uri: window.location.origin,
  });
  if (token) params.set('id_token_hint', token);
  window.location.href = `${OIDC_END_SESSION_URL}?${params}`;
}

export function isAuthenticated(): boolean {
  return accessToken !== '' && expiresAt > Date.now();
}

export function getAccessToken(): string {
  return accessToken;
}

export function getUserName(): string {
  return userName;
}
